import { useEffect, useState } from "react";
import { Download, X, Share, Smartphone, MoreVertical, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

function isIOS() {
  if (typeof window === "undefined") return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

export default function InstallPWA() {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    setInstalled(standalone);

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
      toast.success("Suntelco instalada en tu dispositivo.");
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  // Hide once the app is running as installed PWA
  if (installed) return null;

  const handleInstall = async () => {
    if (deferred) {
      try {
        deferred.prompt();
        const { outcome } = await deferred.userChoice;
        if (outcome !== "accepted") toast.info("Instalación cancelada.");
        setDeferred(null);
        return;
      } catch { /* fall through to manual help */ }
    }
    setShowHelp(true);
  };

  const ios = isIOS();

  return (
    <>
      <button
        type="button"
        onClick={handleInstall}
        data-testid="install-pwa"
        className="w-full rounded-2xl p-4 flex items-center gap-3 border-2 border-[#F5A623] bg-gradient-to-r from-[#FFF6E6] to-white text-left hover:shadow-lg transition-all"
      >
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#FF8A1F] to-[#F5A623] text-white flex items-center justify-center flex-shrink-0">
          <Download size={22} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-extrabold text-base text-[#0B2A4A] leading-tight">
            Instalar Suntelco en este móvil
          </div>
          <div className="text-xs text-slate-600 mt-0.5">
            Acceso directo desde tu pantalla de inicio · Notificaciones diarias
          </div>
        </div>
        <span className="text-xs font-bold text-[#0B2A4A] px-3 py-2 rounded-lg bg-[#F5A623]/15 whitespace-nowrap">
          Instalar
        </span>
      </button>

      <Dialog open={showHelp} onOpenChange={setShowHelp}>
        <DialogContent
          className="max-w-md p-0 overflow-hidden z-[900]"
          data-testid="install-help-dialog"
        >
          <div className="p-5 border-b border-slate-200 bg-gradient-to-r from-[#0B2A4A] to-[#0E78C2] text-white">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-[#FFC740]">
              Instalar Suntelco
            </div>
            <DialogTitle asChild>
              <div className="font-display font-extrabold text-xl leading-tight mt-1 inline-flex items-center gap-2">
                <Smartphone size={20} /> Cómo instalar la app
              </div>
            </DialogTitle>
          </div>
          <div className="p-5 space-y-4 text-sm text-slate-700">
            {ios ? (
              <>
                <p className="text-slate-500 text-xs uppercase tracking-widest font-semibold">Safari en iPhone / iPad</p>
                <ol className="space-y-3">
                  <li className="flex gap-3">
                    <span className="w-6 h-6 rounded-full bg-[#0E78C2] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">1</span>
                    <span>Pulsa el icono <strong>Compartir</strong> <Share size={14} className="inline mx-0.5" /> en la barra inferior de Safari.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="w-6 h-6 rounded-full bg-[#0E78C2] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">2</span>
                    <span>Baja en el menú y toca <strong>“Añadir a pantalla de inicio”</strong>.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="w-6 h-6 rounded-full bg-[#0E78C2] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">3</span>
                    <span>Toca <strong>Añadir</strong> arriba a la derecha. Suntelco quedará en tu pantalla de inicio.</span>
                  </li>
                </ol>
              </>
            ) : (
              <>
                <p className="text-slate-500 text-xs uppercase tracking-widest font-semibold">Chrome en Android</p>
                <ol className="space-y-3">
                  <li className="flex gap-3">
                    <span className="w-6 h-6 rounded-full bg-[#F5A623] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">1</span>
                    <span>Pulsa el icono de <strong>menú</strong> <MoreVertical size={14} className="inline mx-0.5" /> arriba a la derecha del navegador.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="w-6 h-6 rounded-full bg-[#F5A623] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">2</span>
                    <span>Toca <strong>“Instalar app”</strong> o <strong>“Añadir a pantalla de inicio”</strong> <ArrowDown size={14} className="inline mx-0.5" />.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="w-6 h-6 rounded-full bg-[#F5A623] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">3</span>
                    <span>Confirma <strong>Instalar</strong>. Suntelco aparecerá con su icono en tu pantalla de inicio y se abrirá a pantalla completa.</span>
                  </li>
                </ol>
                <p className="text-xs text-slate-500 mt-2 leading-relaxed border-t border-dashed border-slate-200 pt-3">
                  Si no ves “Instalar app”, es posible que ya la tengas instalada, o que necesites interactuar unos segundos más con la web. También funciona en <strong>Chrome</strong>, <strong>Edge</strong> y <strong>Samsung Internet</strong>.
                </p>
              </>
            )}
          </div>
          <div className="p-4 bg-slate-50 border-t border-slate-200 flex justify-end">
            <button
              type="button"
              onClick={() => setShowHelp(false)}
              data-testid="install-help-close"
              className="brutal-btn-dark inline-flex items-center gap-1.5 text-sm px-4 py-2"
            >
              <X size={14} /> Entendido
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
